/**
 * Shared error type for SharePoint data access.
 *
 * Lives in its own module so both the direct-Graph client (graphClient.ts, used
 * by internal staff) and the backend-API client (apiClient.ts, used by AP
 * guests) can throw the same error shape without a circular import. The UI's
 * error handling keys off `statusCode` + `message`.
 *
 * `code` is the fourth field and the newest: 35-02 gave every identity refusal a
 * stable, greppable name (`AP_IDENTITY_NO_MATCH`, `AP_IDENTITY_NO_APCODE`,
 * `AP_INACTIVE`, `AP_IDENTITY_FILTER_MISMATCH`) in an ADDITIVE response body —
 * `{ error: { message, code, ...details } }`. Before this change `apiClient`
 * read only `data.error.message` and dropped every other key on the floor, so
 * the reason the server refused never reached the screen. It does now.
 *
 * It is always a string: `''` when the failure carried no code (a network
 * error, a raw Graph failure, or any pre-35-02 response). Nothing may branch on
 * its ABSENCE meaning anything other than "no named reason was supplied".
 */
export class GraphApiError extends Error {
  public readonly statusCode: number;
  public readonly requestId: string;
  /** The backend's named reason, or `''` when it supplied none. */
  public readonly code: string;

  constructor(statusCode: number, message: string, requestId = '', code = '') {
    super(message);
    this.name = 'GraphApiError';
    this.statusCode = statusCode;
    this.requestId = requestId;
    this.code = code;
  }
}
